import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraInput, SpecraOverlayDialog, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { projectDataClient } from '../../lib/connect/clients';
const ids = Testids.proposalContextReconciliation;
const denied = (error)=>error instanceof ConnectError && [
        Code.NotFound,
        Code.PermissionDenied,
        Code.Unauthenticated
    ].includes(error.code);
const contextLabels = {
    solution: 'Solution',
    win_theme: 'Win theme',
    win_themes: 'Win themes',
    staffing: 'Staffing',
    management_approach: 'Management approach',
    output: 'Output',
    capture_solution_revisions: 'Solution',
    manual_material: 'Management material'
};
function fieldLabel(key) {
    const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase().replace(/\blcat\b/gi, 'LCAT').replace(/\bclins?\b/gi, (value)=>value.toUpperCase()).replace(/\bpws\b/gi, 'PWS');
    return label.charAt(0).toUpperCase() + label.slice(1);
}
function changedFieldLabel(path) {
    return path.split('.').map((part)=>{
        const indexed = /^(source|staffing)\[(\d+)\]$/.exec(part);
        if (indexed) return `${fieldLabel(indexed[1])} ${Number(indexed[2]) + 1}`;
        if (part === 'hours_centi') return 'Hours';
        return contextLabels[part] ?? fieldLabel(part);
    }).join(' · ');
}
export function ProposalContextContents({ content }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            content?.sections.map((section)=>/*#__PURE__*/ _jsxs("section", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            size: "sm",
                            children: contextLabels[section.key] ?? 'Context'
                        }),
                        section.wording && /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            className: "break-words whitespace-pre-wrap",
                            children: [
                                "Proposal wording: ",
                                section.wording
                            ]
                        }),
                        section.sources.map((source)=>/*#__PURE__*/ _jsxs("section", {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "break-words",
                                        size: "meta",
                                        children: contextLabels[source.kind] ?? 'Source'
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        className: "break-words",
                                        size: "meta",
                                        children: [
                                            "Source: ",
                                            source.id
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        className: "break-words",
                                        size: "meta",
                                        children: [
                                            "Version: ",
                                            source.versionId
                                        ]
                                    }),
                                    Object.entries({
                                        approach: source.approach,
                                        intendedOutcomes: source.intendedOutcomes,
                                        rationale: source.rationale,
                                        narrative: source.narrative,
                                        limitations: source.limitations
                                    }).filter(([, value])=>value).map(([key, value])=>/*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            className: "break-words whitespace-pre-wrap",
                                            children: [
                                                fieldLabel(key),
                                                ": ",
                                                value
                                            ]
                                        }, key))
                                ]
                            }, `${source.kind}:${source.id}:${source.versionId}`))
                    ]
                }, section.key)),
            content?.staffing?.map((line)=>/*#__PURE__*/ _jsxs("section", {
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            className: "break-words",
                            children: [
                                line.subjectLabel,
                                " · ",
                                Number(line.hoursCenti) / 100,
                                " hours ·",
                                ' ',
                                fieldLabel(line.subjectKind)
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            className: "break-words",
                            size: "meta",
                            children: [
                                "Line: ",
                                line.lineId,
                                " · Person/provider reference: ",
                                line.subjectRef || 'Not specified',
                                " · Rate evidence: ",
                                line.rateSourceLabel || 'Not specified'
                            ]
                        })
                    ]
                }, line.lineId))
        ]
    });
}
export function ProposalContextReconciliation({ projectId }) {
    const { user } = useAuth();
    if (!user) return null;
    return /*#__PURE__*/ _jsx(ReconciliationPanel, {
        projectId: projectId,
        account: [
            user.tenant_id ?? '',
            user.id
        ]
    }, JSON.stringify([
        user.tenant_id,
        user.id,
        projectId
    ]));
}
function ReconciliationPanel({ projectId, account }) {
    const [selection, setSelection] = useState('');
    const [action, setAction] = useState('');
    const [basis, setBasis] = useState('');
    const [wording, setWording] = useState({});
    const [mappings, setMappings] = useState([]);
    const [message, setMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const submitting = useRef(false);
    const comparison = useRef({
        decision: '',
        upstream: ''
    });
    const request = useRef(undefined);
    const query = useQuery({
        queryKey: [
            'proposal-context-reconciliation',
            ...account,
            projectId
        ],
        queryFn: async ({ signal })=>{
            try {
                const list = await projectDataClient.listProposalContexts({
                    proposalProjectId: projectId
                }, {
                    signal
                });
                let writable = false;
                try {
                    await projectDataClient.checkProposalContextAccess({
                        proposalProjectId: projectId,
                        write: true
                    }, {
                        signal
                    });
                    writable = true;
                } catch (error) {
                    if (!denied(error)) throw error;
                }
                const reviews = await Promise.all(list.adoptions.map(async (adoption)=>{
                    try {
                        return (await projectDataClient.getProposalContextReview({
                            proposalProjectId: projectId,
                            adoptionId: adoption.id
                        }, {
                            signal
                        })).review;
                    } catch (error) {
                        if (denied(error)) return undefined;
                        throw error;
                    }
                }));
                return {
                    writable,
                    reviews: reviews.filter((review)=>!!review && review.adoption?.proposalProjectId === projectId)
                };
            } catch (error) {
                if (denied(error)) return {
                    writable: false,
                    reviews: []
                };
                throw error;
            }
        },
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchInterval: 15000,
        refetchOnWindowFocus: 'always'
    });
    // Fail closed during every authority refresh, including when a dialog is open.
    const data = !query.isError && !query.isFetching ? query.data : undefined;
    const selected = data?.reviews.find((review)=>review.adoption?.id === selection);
    function clear() {
        setAction('');
        setBasis('');
        setWording({});
        setMappings([]);
        request.current = undefined;
    }
    function close() {
        if (!submitting.current) {
            setSelection('');
            clear();
        }
    }
    function open(review) {
        clear();
        setMessage('');
        setSelection(review.adoption?.id ?? '');
        comparison.current = {
            decision: review.accepted?.id ?? '',
            upstream: review.upstream?.versionId ?? ''
        };
        setWording(Object.fromEntries((review.accepted?.content?.sections ?? []).map((section)=>[
                section.key,
                section.wording || section.sources.map((source)=>source.approach || source.narrative).filter(Boolean).join('\n')
            ])));
        setMappings((review.accepted?.content?.staffing ?? []).map((line)=>{
            const prior = review.accepted?.mappings.find((mapping)=>mapping.lineId === line.lineId);
            return prior ? {
                ...prior
            } : {
                lineId: line.lineId,
                lcatId: '',
                hoursCenti: line.hoursCenti,
                provider: '',
                period: '',
                task: '',
                pricingStructure: '',
                evidenceGap: 'LCAT mapping and qualification evidence unresolved',
                availabilityGap: 'Availability not confirmed'
            };
        }));
    }
    const comparisonChanged = !!selected && (comparison.current.decision !== (selected.accepted?.id ?? '') || comparison.current.upstream !== (selected.upstream?.versionId ?? ''));
    const isStaffing = selected?.adoption?.sourceKind === 'staffing';
    const valid = !comparisonChanged && !!data?.writable && !!selected && !!action && !!basis.trim() && basis.length <= 8000 && (action === 'accept_new' || !!selected.accepted) && (action !== 'edit' || (isStaffing ? mappings.length > 0 && mappings.every((mapping)=>Number(mapping.hoursCenti) > 0 && Number(mapping.hoursCenti) <= 100000000 && Number.isSafeInteger(Number(mapping.hoursCenti)) && !!mapping.availabilityGap.trim() && (!!mapping.lcatId || !!mapping.evidenceGap.trim())) : Object.values(wording).length > 0 && Object.values(wording).every((value)=>value.trim() && value.length <= 50000)));
    async function save() {
        if (!valid || !selected || !action || submitting.current) return;
        submitting.current = true;
        setSaving(true);
        setMessage('');
        const payload = {
            proposalProjectId: projectId,
            adoptionId: selection,
            expectedDecisionId: comparison.current.decision,
            expectedUpstreamVersionId: comparison.current.upstream,
            action,
            basis: basis.trim(),
            wording: action === 'edit' && !isStaffing ? Object.entries(wording).map(([sectionKey, value])=>({
                    sectionKey,
                    wording: value
                })) : [],
            mappings: action === 'edit' && isStaffing ? mappings : []
        };
        const signature = JSON.stringify(payload, (_, value)=>typeof value === 'bigint' ? value.toString() : value);
        if (request.current?.payload !== signature) request.current = {
            payload: signature,
            id: crypto.randomUUID()
        };
        try {
            await projectDataClient.decideProposalContext({
                ...payload,
                requestId: request.current.id
            });
            setSelection('');
            clear();
            setMessage('Proposal decision recorded. Accepted history is preserved.');
        } catch (error) {
            if (error instanceof ConnectError && error.code === Code.Aborted) {
                setSelection('');
                clear();
                setMessage('The comparison changed. Review the refreshed source and decide again.');
            } else if (denied(error)) {
                setSelection('');
                clear();
                setMessage('Context unavailable.');
            } else setMessage('Decision could not be saved. Please retry.');
        } finally{
            await query.refetch();
            submitting.current = false;
            setSaving(false);
        }
    }
    function updateMapping(index, key, value) {
        const hoursCenti = Math.round(Number(value) * 100);
        setMappings((current)=>current.map((mapping, i)=>i === index ? {
                    ...mapping,
                    [key]: key === 'hoursCenti' ? BigInt(Number.isSafeInteger(hoursCenti) ? hoursCenti : 0) : value
                } : mapping));
    }
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": ids.root,
        className: "max-h-96 shrink-0 overflow-y-auto",
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: "Proposal intelligence and staffing decisions"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    className: "break-words",
                    children: "Solicitation authority defines obligations. Proposal owns accepted decisions. Upstream Pursuit context offers suggestions. Reusable evidence supports a claim; it does not prove compliance or availability."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": ids.refresh,
                    variant: "outline",
                    disabled: query.isFetching || saving,
                    onClick: ()=>void query.refetch(),
                    children: "Refresh comparison"
                }),
                query.isFetching && /*#__PURE__*/ _jsx("output", {
                    "data-testid": ids.loading,
                    "data-loading": "true",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "break-words",
                        children: "Loading comparison… A slow connection may take a moment."
                    })
                }),
                query.isError && /*#__PURE__*/ _jsxs("section", {
                    "data-testid": ids.error,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            className: "break-words",
                            role: "alert",
                            children: "Comparison could not be loaded."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.retry,
                            onClick: ()=>void query.refetch(),
                            children: "Retry comparison"
                        })
                    ]
                }),
                data && !data.reviews.length && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    className: "break-words",
                    children: "No Proposal decisions available."
                }),
                data?.reviews.map((review)=>/*#__PURE__*/ _jsxs("section", {
                        "data-testid": ids.item,
                        "data-adoption-id": review.adoption?.id,
                        "data-source-kind": review.adoption?.sourceKind,
                        className: "min-w-0 space-y-3",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraHeading, {
                                size: "sm",
                                className: "break-words",
                                children: [
                                    contextLabels[review.adoption?.sourceKind ?? ''] ?? 'Context',
                                    " · Source:",
                                    ' ',
                                    review.adoption?.sourceId
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                className: "break-words",
                                size: "meta",
                                children: [
                                    "Pursuit: ",
                                    review.adoption?.captureProjectId,
                                    " · Original adoption version:",
                                    ' ',
                                    review.adoption?.originalVersionId
                                ]
                            }),
                            review.pending && /*#__PURE__*/ _jsx(SpecraBadge, {
                                "data-testid": ids.pending,
                                variant: "warning",
                                children: "Pending reconciliation"
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                className: "grid grid-cols-1 gap-4 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("section", {
                                        "data-testid": ids.accepted,
                                        className: "min-w-0 space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                                size: "sm",
                                                children: "Proposal-owned accepted decision"
                                            }),
                                            review.accepted ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        className: "break-words",
                                                        children: [
                                                            "Accepted source version: ",
                                                            review.accepted.acceptedVersionId
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        className: "break-words",
                                                        children: [
                                                            "Last reviewed upstream version: ",
                                                            review.accepted.reviewedVersionId
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(ProposalContextContents, {
                                                        content: review.accepted.content
                                                    }),
                                                    review.adoption?.sourceKind === 'staffing' && !review.accepted.mappings.length && /*#__PURE__*/ _jsx(SpecraText, {
                                                        as: "p",
                                                        className: "break-words",
                                                        children: "LCAT mapping, qualifications, period, tasks, pricing structure and availability remain unresolved. Edit accepted staffing to record mappings and gaps."
                                                    }),
                                                    review.accepted.mappings.map((mapping)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                                            as: "p",
                                                            className: "break-words",
                                                            children: [
                                                                mapping.lineId,
                                                                " → LCAT ",
                                                                mapping.lcatId || 'Unmapped',
                                                                " ·",
                                                                ' ',
                                                                Number(mapping.hoursCenti) / 100,
                                                                " hours · Provider:",
                                                                ' ',
                                                                mapping.provider || 'Unresolved',
                                                                " · Period:",
                                                                ' ',
                                                                mapping.period || 'Unresolved',
                                                                " · Task: ",
                                                                mapping.task || 'Unresolved',
                                                                " · Pricing: ",
                                                                mapping.pricingStructure || 'Unresolved',
                                                                " · Evidence gap:",
                                                                ' ',
                                                                mapping.evidenceGap || 'None recorded',
                                                                " · Availability:",
                                                                ' ',
                                                                mapping.availabilityGap
                                                            ]
                                                        }, mapping.lineId))
                                                ]
                                            }) : /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                className: "break-words",
                                                children: "No formal Proposal decision accepted."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("section", {
                                        "data-testid": ids.upstream,
                                        className: "min-w-0 space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                                size: "sm",
                                                children: "Upstream Pursuit context"
                                            }),
                                            /*#__PURE__*/ _jsxs(SpecraText, {
                                                as: "p",
                                                className: "break-words",
                                                children: [
                                                    "Current upstream version: ",
                                                    review.upstream?.versionId
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(ProposalContextContents, {
                                                content: review.upstream
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                className: "break-words",
                                "data-testid": ids.changed,
                                children: [
                                    "Changed fields:",
                                    ' ',
                                    review.changedFields.map(changedFieldLabel).join(', ') || 'No content differences'
                                ]
                            }),
                            review.adoption?.sourceKind === 'staffing' && /*#__PURE__*/ _jsxs("section", {
                                "data-testid": ids.requirements,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraHeading, {
                                        size: "sm",
                                        children: "Solicitation authority · available required structure"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "break-words",
                                        children: "Compare LCATs, key-person obligations, periods, tasks and CLIN pricing below. Missing structure remains unresolved; early hours and role estimates do not satisfy named-person obligations."
                                    }),
                                    !review.requirements.length && /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "break-words",
                                        children: "No solicitation structure available. Requirements and qualification evidence remain unresolved."
                                    }),
                                    review.requirements.map((requirement)=>/*#__PURE__*/ _jsxs("section", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "p",
                                                    className: "break-words",
                                                    children: [
                                                        fieldLabel(requirement.kind),
                                                        " · Row: ",
                                                        requirement.id,
                                                        " · Revision:",
                                                        ' ',
                                                        requirement.version
                                                    ]
                                                }),
                                                Object.entries(requirement.fields).map(([key, value])=>/*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        className: "break-words",
                                                        children: [
                                                            fieldLabel(key),
                                                            ": ",
                                                            value
                                                        ]
                                                    }, key))
                                            ]
                                        }, `${requirement.kind}:${requirement.id}`))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("section", {
                                "data-testid": ids.history,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraHeading, {
                                        size: "sm",
                                        children: "Immutable accepted history"
                                    }),
                                    review.history.map((decision)=>/*#__PURE__*/ _jsxs("section", {
                                            "data-decision-id": decision.id,
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "p",
                                                    className: "break-words",
                                                    children: [
                                                        decision.action === 'accept_new' ? 'Accepted new upstream' : decision.action === 'keep_accepted' ? 'Kept accepted' : 'Edited accepted decision',
                                                        ' ',
                                                        "· Actor: ",
                                                        decision.actorId,
                                                        " · Time: ",
                                                        decision.decidedAt?.toDate().toISOString(),
                                                        ' ',
                                                        "· Basis: ",
                                                        decision.basis
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "p",
                                                    className: "break-words",
                                                    size: "meta",
                                                    children: [
                                                        "Decision: ",
                                                        decision.id,
                                                        " · Parent: ",
                                                        decision.parentId || 'First acceptance',
                                                        " · Accepted source: ",
                                                        decision.acceptedVersionId,
                                                        " · Reviewed upstream:",
                                                        ' ',
                                                        decision.reviewedVersionId
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(ProposalContextContents, {
                                                    content: decision.content
                                                }),
                                                decision.mappings.map((mapping)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        className: "break-words",
                                                        children: [
                                                            mapping.lineId,
                                                            ": ",
                                                            Number(mapping.hoursCenti) / 100,
                                                            " hours · LCAT:",
                                                            ' ',
                                                            mapping.lcatId || 'Unmapped',
                                                            " · Provider: ",
                                                            mapping.provider || 'Unresolved',
                                                            ' ',
                                                            "· Period: ",
                                                            mapping.period || 'Unresolved',
                                                            " · Task:",
                                                            ' ',
                                                            mapping.task || 'Unresolved',
                                                            " · Pricing:",
                                                            ' ',
                                                            mapping.pricingStructure || 'Unresolved',
                                                            " · Evidence:",
                                                            ' ',
                                                            mapping.evidenceGap || 'None recorded',
                                                            " · Availability:",
                                                            ' ',
                                                            mapping.availabilityGap
                                                        ]
                                                    }, mapping.lineId))
                                            ]
                                        }, decision.id))
                                ]
                            }),
                            data.writable && /*#__PURE__*/ _jsxs(SpecraButton, {
                                "data-testid": ids.open,
                                disabled: saving,
                                onClick: ()=>open(review),
                                children: [
                                    "Review ",
                                    contextLabels[review.adoption?.sourceKind ?? ''] ?? 'context'
                                ]
                            })
                        ]
                    }, review.adoption?.id)),
                saving && /*#__PURE__*/ _jsx("output", {
                    "data-loading": "true",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "break-words",
                        children: "Recording decision… Please wait."
                    })
                }),
                message && /*#__PURE__*/ _jsx("output", {
                    "data-testid": ids.message,
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "break-words",
                        children: message
                    })
                }),
                /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                    open: !!selected,
                    onOpenChange: (next)=>{
                        if (!next) close();
                    },
                    title: "Reconcile Proposal decision",
                    testId: ids.dialog,
                    size: "lg",
                    footer: /*#__PURE__*/ _jsxs("section", {
                        className: "flex flex-wrap gap-2 p-4",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.cancel,
                                variant: "outline",
                                disabled: saving,
                                onClick: close,
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.back,
                                variant: "outline",
                                disabled: saving,
                                onClick: ()=>setAction(''),
                                children: "Back"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.submit,
                                disabled: !valid || saving,
                                isLoading: saving,
                                onClick: ()=>void save(),
                                children: "Record Proposal decision"
                            })
                        ]
                    }),
                    children: [
                        selected && /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-3",
                            children: [
                                comparisonChanged && /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "break-words",
                                    role: "alert",
                                    children: "The comparison changed. Cancel and reopen to review the current source before deciding."
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    className: "break-words",
                                    children: [
                                        "Source ",
                                        selected.adoption?.sourceId,
                                        " · Accepted",
                                        ' ',
                                        selected.accepted?.acceptedVersionId || 'None',
                                        " · Current",
                                        ' ',
                                        selected.upstream?.versionId,
                                        ". Proposal edits retain this lineage and never write back to Pursuit."
                                    ]
                                }),
                                [
                                    'accept_new',
                                    'keep_accepted',
                                    'edit'
                                ].map((value)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": ids.action,
                                        "data-action": value,
                                        variant: "outline",
                                        "aria-pressed": action === value,
                                        disabled: saving || value !== 'accept_new' && !selected.accepted,
                                        onClick: ()=>setAction(value),
                                        children: value === 'accept_new' ? 'Accept new upstream' : value === 'keep_accepted' ? 'Keep accepted' : 'Edit accepted Proposal decision'
                                    }, value)),
                                /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    "data-testid": ids.basis,
                                    "aria-label": "Decision basis",
                                    placeholder: "Why is this the Proposal position?",
                                    value: basis,
                                    disabled: saving,
                                    onChange: (event)=>setBasis(event.target.value)
                                }),
                                action === 'accept_new' && /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "break-words",
                                    children: "Accept the exact current upstream content shown above. Existing mappings are replaced; unresolved staffing obligations require review."
                                }),
                                action === 'edit' && !isStaffing && Object.entries(wording).map(([key, value])=>/*#__PURE__*/ _jsx(SpecraTextarea, {
                                        "data-testid": ids.wording,
                                        "data-section-key": key,
                                        "aria-label": `Proposal wording ${contextLabels[key] ?? 'Context'}`,
                                        value: value,
                                        disabled: saving,
                                        onChange: (event)=>setWording((current)=>({
                                                    ...current,
                                                    [key]: event.target.value
                                                }))
                                    }, key)),
                                action === 'edit' && isStaffing && mappings.map((mapping, index)=>/*#__PURE__*/ _jsxs("section", {
                                        "data-testid": ids.mapping,
                                        "data-line-id": mapping.lineId,
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraText, {
                                                as: "p",
                                                className: "break-words",
                                                children: [
                                                    selected.accepted?.content?.staffing.find((line)=>line.lineId === mapping.lineId)?.subjectLabel,
                                                    ' ',
                                                    "· Early assumption line ",
                                                    mapping.lineId
                                                ]
                                            }),
                                            [
                                                [
                                                    'lcatId',
                                                    'LCAT row ID (available solicitation rows above)',
                                                    ids.lcat
                                                ],
                                                [
                                                    'hoursCenti',
                                                    'Revised hours',
                                                    ids.hours
                                                ],
                                                [
                                                    'provider',
                                                    'Proposed provider',
                                                    ids.provider
                                                ],
                                                [
                                                    'period',
                                                    'Period mapping',
                                                    ids.period
                                                ],
                                                [
                                                    'task',
                                                    'Task mapping',
                                                    ids.task
                                                ],
                                                [
                                                    'pricingStructure',
                                                    'Pricing / CLIN mapping',
                                                    ids.pricing
                                                ],
                                                [
                                                    'evidenceGap',
                                                    'Unresolved qualification / evidence gaps',
                                                    ids.evidenceGap
                                                ],
                                                [
                                                    'availabilityGap',
                                                    'Availability evidence or unresolved gap',
                                                    ids.availabilityGap
                                                ]
                                            ].map(([key, label, testid])=>/*#__PURE__*/ _jsx(SpecraInput, {
                                                    "data-testid": testid,
                                                    "aria-label": label,
                                                    placeholder: label,
                                                    type: key === 'hoursCenti' ? 'number' : 'text',
                                                    step: key === 'hoursCenti' ? '0.01' : undefined,
                                                    value: key === 'hoursCenti' ? Number(mapping.hoursCenti) / 100 : mapping[key],
                                                    disabled: saving,
                                                    onChange: (event)=>updateMapping(index, key, event.target.value)
                                                }, key))
                                        ]
                                    }, mapping.lineId))
                            ]
                        }),
                        saving && /*#__PURE__*/ _jsx("output", {
                            "data-loading": "true",
                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "break-words",
                                children: "Recording decision… Please wait."
                            })
                        }),
                        message && /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            className: "break-words",
                            role: "alert",
                            children: message
                        })
                    ]
                })
            ]
        })
    });
}
